import { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Grid, { type GridProps, type GridSize } from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import Tooltip from './Tooltip'
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined'
import { Theme } from '@mui/material/styles'

const classes = {
    divider: {
        width: '100%',
        borderColor: 'transparent',
    },
    tooltip: {
        fontSize: (theme: Theme) => theme.typography.body2.fontSize,
    },
    icon: {
        marginLeft: 1,
        transform: 'translateY(3px)',
    },
}

const gridPropKeys = ['xs', 'lg', 'xl', 'newLine', 'fullWidth', 'tooltip'] as const

export const omitGridProps = <T extends Record<string, unknown>>(obj: T): Omit<T, (typeof gridPropKeys)[number]> => {
    const result = { ...obj }
    for (const key of gridPropKeys) {
        delete (result as Record<string, unknown>)[key]
    }
    return result
}

export interface GridItemProps {
    xs?: GridSize
    lg?: GridSize
    xl?: GridSize
    newLine?: boolean
    tooltip?: string
    children?: ReactNode
    sx?: GridProps['sx']
}

/**
 * Every field should be wrapped with this component. It provides with media query sizing, tooltip, and whether its field should always be on the new line
 * @param props
 */
const GridItem: FC<GridItemProps> = (props) => {
    const size: GridProps['size'] = { xs: props.xs || 12, lg: props.lg || 6, xl: props.xl || 4 }

    return (
        <>
            {props.newLine && <Divider component="div" sx={classes.divider} />}
            <Grid size={size} sx={props.sx}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {props.children}
                    {!!props.tooltip && (
                        <Tooltip
                            title={<span style={{ whiteSpace: 'pre-line' }}>{props.tooltip}</span>}
                            sx={{ tooltip: classes.tooltip }}
                            placement="top"
                        >
                            <HelpOutlineOutlined sx={classes.icon} />
                        </Tooltip>
                    )}
                </Box>
            </Grid>
        </>
    )
}

export default GridItem
